import { Component, lazy, Suspense, useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useScroll, useTransform } from "framer-motion";
import { Award, MapPin, Wrench } from "lucide-react";
import { EASE, MaskLines, Reveal } from "@/components/motion/Reveal";

const Number65 = lazy(() => import("@/components/three/Number65"));

class Boundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? <div className="grid h-full place-items-center font-display text-[12rem] font-black text-white">65+</div> : this.props.children;
  }
}

const Counter = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, 65, { duration: 2.2, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView]);
  return <span ref={ref} className="tabular-nums" data-testid="legacy-counter">{n}+</span>;
};

const CHIPS = [
  [Award, "Over 65 years in business"],
  [MapPin, "Rooted in McMinnville, TN"],
  [Wrench, "Sales · Installation · Service"],
];

export const Legacy = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgX = useTransform(scrollYProgress, [0, 1], ["10%", "-30%"]);
  return (
    <section ref={ref} className="relative overflow-hidden border-b border-white/5 py-24 lg:py-32" data-testid="legacy-section">
      <motion.div style={{ x: bgX }} aria-hidden className="pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap font-display text-[18vw] font-black uppercase leading-none tracking-[-0.05em] text-outline opacity-[0.12]">
        65 Years · 65 Years · 65 Years
      </motion.div>
      <div className="pointer-events-none absolute left-[15%] top-1/2 h-[60vh] w-[60vh] -translate-y-1/2 rounded-full bg-volt/10 blur-[140px]" />
      <div className="container-x relative grid items-center gap-8 lg:grid-cols-12">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 1.2, ease: EASE }} className="relative h-[340px] sm:h-[460px] lg:col-span-7 lg:h-[560px]">
          <Boundary>
            <Suspense fallback={null}>
              <Number65 />
            </Suspense>
          </Boundary>
          <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.35em] text-slate-400">Years in business</div>
        </motion.div>
        <div className="lg:col-span-5">
          <Reveal><p className="eyebrow flex items-center gap-3"><span className="h-px w-10 bg-signal" /> A Tennessee Legacy</p></Reveal>
          <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-[-0.03em] text-white sm:text-5xl lg:text-6xl">
            <MaskLines onView lines={[<><span className="text-volt"><Counter /></span> years</>, "of keeping", <span key="o" className="text-outline">customers coming back.</span>]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate-400">For over 65 years, Brewer Amusement has helped local businesses bring in entertainment their customers love — and kept it running. That kind of track record is built one partnership at a time.</p>
            <ul className="mt-8 space-y-3">
              {CHIPS.map(([Icon, label], i) => (
                <motion.li key={label} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1, duration: 0.7, ease: EASE }} className="flex items-center gap-3 rounded-full border border-white/10 bg-ink-800/60 px-4 py-2.5 text-sm text-slate-200 backdrop-blur-md sm:w-fit">
                  <Icon className="h-4 w-4 text-volt" /> {label}
                </motion.li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const LegacyBadge = ({ className = "" }) => (
  <div className={`pointer-events-none relative h-28 w-28 sm:h-32 sm:w-32 ${className}`} data-testid="hero-65-badge">
    <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full animate-[spin_18s_linear_infinite]">
      <defs><path id="badge-circle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
      <text className="fill-slate-300 font-mono text-[9.5px] uppercase tracking-[0.32em]"><textPath href="#badge-circle">Over 65 years in business · Brewer Amusement ·</textPath></text>
    </svg>
    <div className="absolute inset-[26%] grid place-items-center rounded-full border border-volt/40 bg-ink-950/80 shadow-[0_0_40px_-6px_rgba(0,240,255,0.7)] backdrop-blur-md">
      <span className="font-display text-2xl font-black leading-none text-white sm:text-3xl">65<span className="text-signal">+</span></span>
    </div>
  </div>
);
